/**
 * Today Inbox tab — overdue tasks + due-today tasks + cold leads in one feed.
 * Goal: start the day with one screen telling you who to call right now.
 */
import { useEffect, useState } from "react";
import { Phone, MessageSquareText, Mail, Clock, AlertCircle, Snowflake, Gift, RotateCcw, Car, CalendarPlus, Loader2, Copy, Flame } from "lucide-react";
import { api } from "../lib/api";

const KIND_ICON = { call: Phone, text: MessageSquareText, email: Mail, appointment: Clock };

function dueLabel(iso, nowIso) {
  if (!iso) return "—";
  const diff = new Date(iso).getTime() - new Date(nowIso).getTime();
  const m = Math.round(diff / 60000);
  if (m < -60 * 24) return `${Math.abs(Math.round(m / 60 / 24))}d overdue`;
  if (m < 0) return `${Math.abs(Math.round(m / 60))}h overdue`;
  if (m < 60) return `due in ${Math.max(1, m)}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `due in ${h}h`;
  return `due in ${Math.round(h / 24)}d`;
}

export default function TodayInbox({ onOpenLead }) {
  const [data, setData] = useState(null);
  const [celebrations, setCelebrations] = useState([]);
  const [resurrect, setResurrect] = useState([]);
  const [drip, setDrip] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [calBusy, setCalBusy] = useState(false);
  const [calCopied, setCalCopied] = useState(false);
  const [hotSheet, setHotSheet] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [inbox, cel, res, drp, cal, hs] = await Promise.all([
        api.get("/dealer/inbox/today"),
        api.get("/automation/celebrations").catch(() => ({ data: { items: [] } })),
        api.get("/automation/resurrection").catch(() => ({ data: { items: [] } })),
        api.get("/automation/drip-candidates").catch(() => ({ data: { items: [] } })),
        api.get("/dealer/calendar/me").catch(() => ({ data: { token: null } })),
        api.get("/dealer/hot-sheet").catch(() => ({ data: { items: [] } })),
      ]);
      setData(inbox.data);
      setCelebrations(cel.data.items || []);
      setResurrect(res.data.items || []);
      setDrip(drp.data.items || []);
      setCalendar(cal.data);
      setHotSheet(hs.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  // auto-refresh hot sheet every 30s
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data: r } = await api.get("/dealer/hot-sheet");
        setHotSheet(r.items || []);
      } catch { /* noop */ }
    }, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { load(); }, []);

  const rotateCal = async () => {
    setCalBusy(true);
    try {
      const { data: r } = await api.post("/dealer/calendar/rotate-token");
      setCalendar(r);
    } finally { setCalBusy(false); }
  };

  if (loading) return <div className="text-white/60 p-6">Loading today's inbox…</div>;
  if (!data) return null;

  const { overdue, due_today, cold_leads, hot_leads, counts, now } = data;
  const calendarFullUrl = calendar?.url_path
    ? `${window.location.origin}${process.env.REACT_APP_BACKEND_URL ? "" : ""}/api${calendar.url_path.replace(/^\/api/, "")}`
    : null;
  const apiBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const subscribeUrl = calendar?.url_path ? `${apiBase}${calendar.url_path}` : null;

  return (
    <div className="space-y-4" data-testid="today-inbox">
      <div className="grid sm:grid-cols-4 gap-3">
        <KpiCard label="🔥 Hot AI" value={(hot_leads || []).length} icon={Flame} tone="red" />
        <KpiCard label="Overdue" value={counts.overdue} icon={AlertCircle} tone="red" />
        <KpiCard label="Due today" value={counts.due_today} icon={Clock} tone="blue" />
        <KpiCard label="Cold leads" value={counts.cold} icon={Snowflake} tone="amber" />
      </div>

      {(hot_leads || []).length > 0 && (
        <Section title={`🔥 Hot AI Leads (${hot_leads.length})`} tone="red" testId="inbox-hot">
          <div className="px-4 py-2 text-xs text-[#6b7c93] bg-[var(--ssc-paper)] border-b border-[var(--ssc-line)]">
            AI intent score ≥ 80 — these are your highest-priority follow-ups today.
          </div>
          <ul className="divide-y divide-[var(--ssc-line)]">
            {hot_leads.map((l) => (
              <li key={l.id} className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-[var(--ssc-paper)] cursor-pointer"
                  onClick={() => onOpenLead?.(l.id)} data-testid={`inbox-hot-${l.id}`}>
                <Flame size={16} className="text-red-600 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)]">{l.name || "Anonymous"}</div>
                  <div className="text-xs text-[#6b7c93]">
                    Score <strong className="text-red-600">{l.ai_intent_score}</strong>
                    {l.ai_sentiment && <span> · {l.ai_sentiment}</span>}
                    {l.vehicle_title && <span> · {l.vehicle_title}</span>}
                  </div>
                </div>
                <div className="text-xs text-[#6b7c93] shrink-0">{new Date(l.created_at).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {hotSheet.length > 0 && (
        <Section title={`👀 On The Site Right Now (${hotSheet.length})`} tone="blue" testId="inbox-hotsheet">
          <div className="px-4 py-2 text-xs text-[#6b7c93] bg-[var(--ssc-paper)] border-b border-[var(--ssc-line)]">
            Customers currently browsing (last 90 seconds). Refreshes every 30s.
          </div>
          <ul className="divide-y divide-[var(--ssc-line)]">
            {hotSheet.slice(0, 12).map((s) => (
              <li key={s.session_id} className="flex items-start gap-3 px-4 py-3 bg-white">
                <span className="relative flex h-2.5 w-2.5 mt-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)] text-sm">{s.name || "Anonymous visitor"}</div>
                  <div className="text-xs text-[#6b7c93]">
                    {s.page || "/"} {s.vehicle ? `· ${s.vehicle.year} ${s.vehicle.make} ${s.vehicle.model}` : ""}
                  </div>
                </div>
                <div className="text-[10px] text-[#6b7c93] shrink-0">{new Date(s.last_seen_at).toLocaleTimeString()}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={`Overdue (${counts.overdue})`} tone="red" testId="inbox-overdue">
        <TaskList items={overdue} now={now} onOpenLead={onOpenLead} />
      </Section>
      <Section title={`Due Today (${counts.due_today})`} tone="blue" testId="inbox-today">
        <TaskList items={due_today} now={now} onOpenLead={onOpenLead} />
      </Section>
      <Section title={`Cold Uncontacted Leads (${counts.cold})`} tone="amber" testId="inbox-cold">
        {cold_leads.length === 0 ? (
          <div className="text-sm text-white/60 px-4 py-3">No cold leads. Nicely done.</div>
        ) : (
          <ul className="divide-y divide-[var(--ssc-line)]">
            {cold_leads.map((l) => (
              <li key={l.id} className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-[var(--ssc-paper)] cursor-pointer"
                  onClick={() => onOpenLead?.(l.id)} data-testid={`inbox-cold-${l.id}`}>
                <Snowflake size={16} className="text-amber-600 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)]">{l.name || "Anonymous"}</div>
                  <div className="text-xs text-[#6b7c93]">
                    {l.email || "—"} {l.phone && <span>· {l.phone}</span>}
                  </div>
                  {l.vehicle_title && <div className="text-xs text-[var(--ssc-ink)] mt-0.5">{l.vehicle_title}</div>}
                </div>
                <div className="text-xs text-[#6b7c93] shrink-0">{new Date(l.created_at).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {drip.length > 0 && (
        <Section title={`Auto-Drip Candidates (${drip.length})`} tone="blue" testId="inbox-drip">
          <div className="px-4 py-2 text-xs text-[#6b7c93] bg-[var(--ssc-paper)] border-b border-[var(--ssc-line)]">
            New leads with no reply &gt; 24h — open one and use AI Assist to fire a personalized first-touch.
          </div>
          <ul className="divide-y divide-[var(--ssc-line)]">
            {drip.slice(0, 10).map((l) => (
              <li key={l.id} className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-[var(--ssc-paper)] cursor-pointer"
                  onClick={() => onOpenLead?.(l.id)} data-testid={`inbox-drip-${l.id}`}>
                <Mail size={16} className="text-[var(--ssc-blue)] mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)]">{l.name || "Anonymous"}</div>
                  <div className="text-xs text-[#6b7c93]">{l.email || "—"} {l.phone && <span>· {l.phone}</span>}</div>
                  {l.vehicle_title && <div className="text-xs text-[var(--ssc-ink)] mt-0.5">{l.vehicle_title}</div>}
                </div>
                <div className="text-xs text-[#6b7c93] shrink-0">{new Date(l.created_at).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {resurrect.length > 0 && (
        <Section title={`Stale-Lead Resurrection (${resurrect.length})`} tone="amber" testId="inbox-resurrect">
          <div className="px-4 py-2 text-xs text-[#6b7c93] bg-[var(--ssc-paper)] border-b border-[var(--ssc-line)]">
            Lost &gt;30 days ago to Price or "Found elsewhere" — many of these have circled back.
          </div>
          <ul className="divide-y divide-[var(--ssc-line)]">
            {resurrect.slice(0, 10).map((l) => (
              <li key={l.id} className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-[var(--ssc-paper)] cursor-pointer"
                  onClick={() => onOpenLead?.(l.id)} data-testid={`inbox-resurrect-${l.id}`}>
                <RotateCcw size={16} className="text-amber-600 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)]">{l.name || "Anonymous"}</div>
                  <div className="text-xs text-[#6b7c93]">Lost: {l.loss_reason || "—"} · {l.lost_at && new Date(l.lost_at).toLocaleDateString()}</div>
                  {l.vehicle_title && <div className="text-xs text-[var(--ssc-ink)] mt-0.5">{l.vehicle_title}</div>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {celebrations.length > 0 && (
        <Section title={`Birthdays & Anniversaries (${celebrations.length})`} tone="blue" testId="inbox-celebrations">
          <ul className="divide-y divide-[var(--ssc-line)]">
            {celebrations.slice(0, 10).map((l, i) => (
              <li key={`${l.id}-${i}`} className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-[var(--ssc-paper)] cursor-pointer"
                  onClick={() => onOpenLead?.(l.id)} data-testid={`inbox-celebration-${l.id}`}>
                <Gift size={16} className="text-pink-600 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)]">{l.name || "Anonymous"}</div>
                  <div className="text-xs text-[#6b7c93]">
                    {l.kind === "birthday" ? "Birthday" : `${l.years}-year anniversary`}
                    {l.in_days === 0 ? " — today" : ` — in ${l.in_days} day${l.in_days === 1 ? "" : "s"}`}
                    {l.sold_vehicle && <span> · {l.sold_vehicle}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Calendar sync */}
      <section className="rounded-2xl border border-white/15 bg-white/5 p-4" data-testid="inbox-calendar-sync">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/70 inline-flex items-center gap-1.5">
          <CalendarPlus size={11} /> Calendar Sync (Google / Apple / Outlook)
        </div>
        {subscribeUrl ? (
          <div className="mt-2">
            <div className="text-xs text-white/70 mb-1.5">Subscribe to this URL in your calendar app to see every open task automatically:</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                readOnly
                value={subscribeUrl}
                onClick={(e) => e.target.select()}
                className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white font-mono text-xs"
                data-testid="calendar-feed-url"
              />
              <button onClick={() => { navigator.clipboard.writeText(subscribeUrl); setCalCopied(true); setTimeout(() => setCalCopied(false), 1500); }}
                      className="ssc-btn ssc-btn-primary text-xs px-3 inline-flex items-center gap-1" data-testid="calendar-copy">
                <Copy size={12} /> {calCopied ? "Copied!" : "Copy"}
              </button>
              <button onClick={rotateCal} disabled={calBusy} className="ssc-btn ssc-btn-secondary text-xs px-3" data-testid="calendar-rotate">
                {calBusy ? <Loader2 size={12} className="animate-spin" /> : "Rotate URL"}
              </button>
            </div>
            <div className="text-[10px] text-white/50 mt-1.5">Google: Other calendars → "From URL". Apple: File → New Calendar Subscription. Anyone with this URL can see your task titles, so keep it private. Hit "Rotate URL" if you ever share it by mistake.</div>
          </div>
        ) : (
          <div className="mt-2">
            <button onClick={rotateCal} disabled={calBusy} className="ssc-btn ssc-btn-primary text-xs px-3 inline-flex items-center gap-1" data-testid="calendar-enable">
              {calBusy ? <Loader2 size={12} className="animate-spin" /> : <CalendarPlus size={12} />} Enable calendar sync
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function Section({ title, tone, children, testId }) {
  const toneCls = {
    red: "border-red-500/40 bg-red-500/5",
    blue: "border-[var(--ssc-blue)]/40 bg-white/5",
    amber: "border-amber-500/40 bg-amber-500/5",
  }[tone];
  return (
    <section className={`rounded-2xl border ${toneCls} overflow-hidden`} data-testid={testId}>
      <div className="px-4 py-2 text-white font-black text-sm uppercase tracking-wider">{title}</div>
      <div className="bg-white">{children}</div>
    </section>
  );
}

function TaskList({ items, now, onOpenLead }) {
  if (items.length === 0) {
    return <div className="text-sm text-[#6b7c93] px-4 py-3">Nothing here. ✅</div>;
  }
  return (
    <ul className="divide-y divide-[var(--ssc-line)]">
      {items.map((t) => {
        const Icon = KIND_ICON[t.kind] || Clock;
        const overdue = (t.due_at || "") < now;
        return (
          <li key={t.id}
              className="flex items-start gap-3 px-4 py-3 hover:bg-[var(--ssc-paper)] cursor-pointer"
              onClick={() => t.lead?.id && onOpenLead?.(t.lead.id)}
              data-testid={`inbox-task-${t.id}`}>
            <Icon size={16} className={overdue ? "text-red-600 mt-0.5" : "text-[var(--ssc-blue)] mt-0.5"} />
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-[var(--ssc-navy)]">{t.title}</div>
              <div className="text-xs text-[#6b7c93]">
                {t.lead?.name || "Unknown lead"}
                {t.lead?.phone && <span> · {t.lead.phone}</span>}
                {t.lead?.vehicle_title && <span> · {t.lead.vehicle_title}</span>}
              </div>
            </div>
            <div className={`text-xs shrink-0 font-bold ${overdue ? "text-red-600" : "text-[#6b7c93]"}`}>
              {dueLabel(t.due_at, now)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function KpiCard({ label, value, icon: Icon, tone }) {
  const toneCls = {
    red: "bg-red-500/10 border-red-500/40 text-red-200",
    blue: "bg-[var(--ssc-blue)]/10 border-[var(--ssc-blue)]/40 text-[var(--ssc-blue)]",
    amber: "bg-amber-500/10 border-amber-500/40 text-amber-200",
    navy: "bg-white/5 border-white/20 text-white",
  }[tone] || "bg-white/5";
  return (
    <div className={`rounded-2xl border p-4 ${toneCls}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-extrabold uppercase tracking-widest">{label}</div>
        <Icon size={16} />
      </div>
      <div className="text-3xl font-black mt-1 text-white">{value}</div>
    </div>
  );
}
